// Recipe Diet and Pantry badges — port of Swift RecipeView+Helpers dietStatus/pantryStatus.
import type { DietStatus } from './diets';
import type { Id, Pantry } from '../types';

export type BadgeState = 'satisfied' | 'failed' | 'warning' | 'unknown';
export type Badge = { state: BadgeState; key: 'breaks' | 'issue' | 'unreviewed' | 'confirmed' | 'none' | 'unknown' | 'missing' | 'all'; items: string[] };

/**
 * Diet, summarised from the review: an ingredient that breaks a diet → failed (certain); warnings →
 * warning; every diet confirmed by a person → satisfied; otherwise unknown (nothing flagged, not confirmed).
 * Pending edits downgrade "satisfied" to "warning" (Swift draft-uncertainty rule).
 */
export function dietBadge(review: DietStatus[], pendingEdits = false): Badge {
  if (!review.length) return { state: pendingEdits ? 'warning' : 'satisfied', key: 'none', items: [] };
  const breaks = review.filter(s => s.state === 'breaks').map(s => s.diet);
  if (breaks.length) return { state: 'failed', key: 'breaks', items: breaks.slice(0, 3) };
  const issues = review.filter(s => s.state === 'issue').map(s => s.diet);
  if (issues.length) return { state: 'warning', key: 'issue', items: issues.slice(0, 3) };
  const open = review.filter(s => s.state === 'unreviewed').map(s => s.diet);
  if (open.length) return { state: pendingEdits ? 'warning' : 'unknown', key: 'unreviewed', items: open.slice(0, 3) };
  return { state: pendingEdits ? 'warning' : 'satisfied', key: 'confirmed', items: review.map(s => s.diet).slice(0, 3) };
}

/** Pantry: names of up to 3 missing items, ingredients first. */
export function pantryBadge(
  ingredientIds: Id[], utensilIds: Id[], pantry: Pantry, titles: (kind: 'ingredient' | 'utensil', id: Id) => string, pendingEdits = false,
): Badge {
  if (ingredientIds.length + utensilIds.length === 0) return { state: 'unknown', key: 'unknown', items: [] };
  const have = new Set(pantry.items.map(p => `${p.kind}:${p.refId}`));
  const missing = [
    ...ingredientIds.filter(id => !have.has(`ingredient:${id}`)).map(id => titles('ingredient', id)),
    ...utensilIds.filter(id => !have.has(`utensil:${id}`)).map(id => titles('utensil', id)),
  ];
  const unique = [...new Set(missing)].slice(0, 3);
  if (unique.length) return { state: 'failed', key: 'missing', items: unique };
  return { state: pendingEdits ? 'warning' : 'satisfied', key: 'all', items: [] };
}
